/**
 * Resolve pass — step 1 of the pagination model (template spec §7).
 *
 * Turns a template document plus a report data model into a flat, fully-evaluated
 * tree: lifecycle filtered, `visibleWhen` applied, `repeat` expanded, every binding
 * evaluated to a value. The renderer that consumes this knows nothing about JSONata,
 * and nothing about any specific report — which is constraint C2.
 *
 * Everything here is data-driven from the block registry. There is no branch on a
 * block `type` anywhere in this file, and there must never be one: the moment the
 * resolver knows what a `KpiTileRow` is, R2's builder has been undermined.
 */

import type {
  Template,
  BlockManifest,
  BlockInstanceT,
  Lifecycle,
  ThemeTokens,
  PageDef,
} from '@pdmg/reporting-schema';
import {
  evaluateBinding, isTruthy, BindingError, compile, DEFAULT_BUDGET, type Budget,
} from './evaluate.js';

/**
 * Where a rendered value came from, and whether a human can change it.
 *
 * A report is edited by overriding a value in the report DATA and re-deriving —
 * never by typing over the rendered output. So an edit needs the data path behind
 * the value, and only a binding that IS a path has one. `summary.clicks` can be
 * overridden; `summary.clicks / summary.impressions * 100` cannot, because there is
 * no single place to put the answer. This records that distinction rather than
 * leaving the builder to guess it from the expression.
 */
export interface BindingSource {
  /** The binding expression, verbatim. */
  expr: string;
  /**
   * The data path an override targets, or null when the expression computes.
   * Resolved through repeat scope, so `$row.clicks` inside a repeat over
   * `deviceDetails.rows` becomes `deviceDetails.rows[2].clicks`.
   */
  dataPath: string | null;
  /**
   * For a binding that BUILDS a structure rather than reading one value: the data path
   * behind each leaf, keyed by that leaf's position inside the resolved value.
   *
   *   [{"label": "Clicks", "value": overall.delivery.clicks}]
   *     -> { "[0].value": "overall.delivery.clicks" }
   *
   * Without this the four headline KPI tiles on page 1 would be uneditable, because the
   * template assembles them from an array literal — the binding as a whole has no single
   * data path even though every figure in it does.
   */
  leafPaths: Record<string, string>;
}

export interface ResolvedBlock {
  /** Unique per rendered instance — repeat iterations get a suffix. */
  id: string;
  type: string;
  /** Author props merged over the manifest's declared defaults. */
  props: Record<string, unknown>;
  /** Evaluated binding values, keyed by slot. */
  values: Record<string, unknown>;
  /**
   * Provenance for each slot in `values`. Same keys, always — a slot that resolved
   * to null still has a source, and the builder needs it to offer the edit.
   */
  sources: Record<string, BindingSource>;
  manifest: BlockManifest;
  children: ResolvedBlock[];
}

/**
 * What a rendered value came from, read off the compiled expression's own AST.
 *
 * An earlier version matched the source text against a whitelist regex. The AST is
 * strictly better: it cannot be fooled by whitespace or by an expression that merely
 * looks like a path, and it exposes the structure literals the templates actually use.
 * Being wrong in the permissive direction is the dangerous one — offering an edit on a
 * computed value writes to a path the report never reads, so the figure appears to
 * change and then silently reverts on the next render. Anything not recognised here is
 * therefore treated as computed.
 */
interface AstNode {
  type?: string;
  value?: unknown;
  steps?: AstNode[];
  stages?: Array<{ type?: string; expr?: AstNode }>;
  expressions?: AstNode[];
  lhs?: unknown;
}

/** A `type: 'path'` node -> "a.b[2].c", or null if any step is not a plain name. */
function pathFromAst(node: AstNode, scopePaths: Record<string, string>): string | null {
  if (node?.type !== 'path' || !Array.isArray(node.steps) || node.steps.length === 0) return null;

  const parts: string[] = [];
  for (const [i, step] of node.steps.entries()) {
    if (step.type === 'variable') {
      // Only ever legal as the first step: `$row.clicks`, never `a.$row`.
      if (i !== 0) return null;
      const base = scopePaths[`$${String(step.value)}`];
      if (base === undefined) return null;
      parts.push(base);
    } else if (step.type === 'name') {
      parts.push(String(step.value));
    } else {
      return null;
    }
    // `a[2]` arrives as a numeric filter stage. Anything else — a predicate, a sort — is
    // a query, not an address, and cannot take an override.
    for (const stage of step.stages ?? []) {
      if (stage.type !== 'filter' || stage.expr?.type !== 'number') return null;
      parts[parts.length - 1] += `[${String(stage.expr.value)}]`;
    }
  }
  return parts.join('.');
}

/**
 * Walk a structure literal and record the data path behind each leaf.
 *
 * `[` is an array constructor and `{` an object constructor; both keep their element
 * order, so a leaf's position in the AST is its position in the resolved value.
 */
function collectLeaves(
  node: AstNode, prefix: string, scopePaths: Record<string, string>, out: Record<string, string>
): void {
  if (!node || typeof node !== 'object') return;

  const asPath = pathFromAst(node, scopePaths);
  if (asPath !== null) {
    if (prefix !== '') out[prefix] = asPath;
    return;
  }
  if (node.type !== 'unary') return;

  if (node.value === '[') {
    (node.expressions ?? []).forEach((el, i) => collectLeaves(el, `${prefix}[${i}]`, scopePaths, out));
    return;
  }
  if (node.value === '{') {
    for (const pair of (node.lhs as Array<[AstNode, AstNode]>) ?? []) {
      const [key, val] = pair;
      // A computed key gives a leaf no stable address, so it gets no edit.
      if (key?.type !== 'string') continue;
      collectLeaves(val, `${prefix}.${String(key.value)}`, scopePaths, out);
    }
  }
}

function sourceFor(expr: string, scopePaths: Record<string, string>, budget: Budget): BindingSource {
  let ast: AstNode;
  try {
    ast = (compile(expr, budget) as unknown as { ast(): AstNode }).ast();
  } catch {
    // An expression that does not compile is about to fail evaluation anyway, and that
    // error is the useful one. Report it as uneditable rather than pre-empting it.
    return { expr, dataPath: null, leafPaths: {} };
  }
  const leafPaths: Record<string, string> = {};
  collectLeaves(ast, '', scopePaths, leafPaths);
  return { expr, dataPath: pathFromAst(ast, scopePaths), leafPaths };
}

export interface ResolvedPage {
  /** Unique per emitted page — a repeated page gets a suffix. */
  id: string;
  mode: 'fixed' | 'flowing';
  blocks: ResolvedBlock[];
  /** Optional per-page chrome header override; when absent the document-level `header` is used. */
  header?: ResolvedBlock[];
}

export interface ResolvedDocument {
  lifecycle: Lifecycle;
  theme: ThemeTokens;
  header: ResolvedBlock[];
  footer: ResolvedBlock[];
  pages: ResolvedPage[];
}

export interface ResolveOptions {
  lifecycle?: Lifecycle;
  budget?: Budget;
}

/** Manifest prop defaults, applied under the author's explicit props. */
function mergeProps(manifest: BlockManifest, authored: Record<string, unknown> = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, spec] of Object.entries(manifest.props ?? {})) {
    if (spec.default !== undefined) out[key] = spec.default;
  }
  return { ...out, ...authored };
}

/**
 * Should this block be dropped because the data it needs isn't available yet?
 *
 * Only applies to manifests declaring `unavailableRendering: 'hide'`. The other two
 * modes ('tbd', 'zero') are the block component's business at render time, because
 * they still produce output.
 *
 * The guard on `required.length` matters: a container like SectionCard declares no
 * bindings at all, and "every required binding is null" is vacuously true of an empty
 * set. Without it, every container in the catalog would silently disappear.
 */
function isUnavailable(manifest: BlockManifest, values: Record<string, unknown>): boolean {
  if (manifest.unavailableRendering !== 'hide') return false;
  const required = Object.entries(manifest.bindings ?? {}).filter(([, spec]) => spec.required);
  if (required.length === 0) return false;
  return required.every(([slot]) => {
    const v = values[slot];
    return v === null || v === undefined || (Array.isArray(v) && v.length === 0);
  });
}

function supportsLifecycle(
  manifest: BlockManifest,
  block: Pick<BlockInstanceT, 'lifecycle'>,
  stage: Lifecycle
): boolean {
  // The manifest bounds where a block *can* appear; the instance narrows it further.
  if (!manifest.lifecycle.includes(stage)) return false;
  if (block.lifecycle && !block.lifecycle.includes(stage)) return false;
  return true;
}

async function resolveBlock(
  block: BlockInstanceT,
  ctx: {
    data: unknown;
    scope: Record<string, unknown>;
    /** Repeat variable -> data path of the bound item. See dataPathFor(). */
    scopePaths: Record<string, string>;
    byType: Map<string, BlockManifest>;
    stage: Lifecycle;
    budget: Budget;
    path: string;
  }
): Promise<ResolvedBlock[]> {
  const manifest = ctx.byType.get(block.type);
  if (!manifest) {
    // Publish-time validation should have caught this. Reaching here means an
    // unpublished or hand-edited document; fail loudly rather than skip silently.
    throw new BindingError(`unknown block type "${block.type}"`, '', ctx.path);
  }

  if (!supportsLifecycle(manifest, block, ctx.stage)) return [];

  // repeat expands to N instances, each with its own scope variable
  const iterations: Array<{
    scope: Record<string, unknown>;
    scopePaths: Record<string, string>;
    suffix: string;
  }> = [];
  if (block.repeat) {
    const over = await evaluateBinding(block.repeat.over, ctx.data, ctx.scope, ctx.budget, ctx.path);
    let items: unknown[] = over === undefined || over === null ? [] : Array.isArray(over) ? over : [over];
    if (block.repeat.max !== undefined) items = items.slice(0, block.repeat.max);
    if (items.length === 0 && block.repeat.emptyBehaviour === 'hide') return [];
    // The collection's own path, so each item's path is that plus its index.
    const overPath = sourceFor(block.repeat.over, ctx.scopePaths, ctx.budget).dataPath;
    items.forEach((item, i) => {
      iterations.push({
        scope: { ...ctx.scope, [block.repeat!.as]: item },
        scopePaths: overPath === null
          ? ctx.scopePaths
          : { ...ctx.scopePaths, [`$${block.repeat!.as}`]: `${overPath}[${i}]` },
        suffix: `#${i}`,
      });
    });
  } else {
    iterations.push({ scope: ctx.scope, scopePaths: ctx.scopePaths, suffix: '' });
  }

  const out: ResolvedBlock[] = [];
  for (const iter of iterations) {
    if (block.visibleWhen) {
      const v = await evaluateBinding(block.visibleWhen, ctx.data, iter.scope, ctx.budget, ctx.path);
      if (!isTruthy(v)) continue;
    }

    const values: Record<string, unknown> = {};
    const sources: Record<string, BindingSource> = {};
    for (const [slot, src] of Object.entries(block.bindings ?? {})) {
      sources[slot] = sourceFor(src, iter.scopePaths, ctx.budget);
      const v = await evaluateBinding(src, ctx.data, iter.scope, ctx.budget, `${ctx.path}.${slot}`);
      // JSONata returns undefined for a path matching nothing. The data model's
      // convention is that an unknown metric is null, so normalise here — the
      // difference between "no such path" and "not yet measured" must not leak
      // into the components as two different absences.
      values[slot] = v === undefined ? null : v;
    }

    if (isUnavailable(manifest, values)) continue;

    const children: ResolvedBlock[] = [];
    for (const [i, child] of (block.children ?? []).entries()) {
      children.push(
        ...(await resolveBlock(child, {
          ...ctx, scope: iter.scope, scopePaths: iter.scopePaths,
          path: `${ctx.path}.children[${i}]`,
        }))
      );
    }

    out.push({
      id: block.id + iter.suffix,
      type: block.type,
      props: mergeProps(manifest, block.props),
      values,
      sources,
      manifest,
      children,
    });
  }
  return out;
}

async function resolvePage(
  page: PageDef,
  ctx: {
    data: unknown;
    byType: Map<string, BlockManifest>;
    stage: Lifecycle;
    budget: Budget;
  }
): Promise<ResolvedPage[]> {
  if (page.lifecycle && !page.lifecycle.includes(ctx.stage)) return [];

  const iterations: Array<{
    scope: Record<string, unknown>;
    scopePaths: Record<string, string>;
    suffix: string;
  }> = [];
  if (page.repeat) {
    const over = await evaluateBinding(page.repeat.over, ctx.data, {}, ctx.budget, `page:${page.id}`);
    let items: unknown[] = over === undefined || over === null ? [] : Array.isArray(over) ? over : [over];
    if (page.repeat.max !== undefined) items = items.slice(0, page.repeat.max);
    if (items.length === 0 && page.repeat.emptyBehaviour === 'hide') return [];
    const overPath = sourceFor(page.repeat.over, {}, ctx.budget).dataPath;
    items.forEach((item, i) => {
      iterations.push({
        scope: { [page.repeat!.as]: item },
        scopePaths: overPath === null ? {} : { [`$${page.repeat!.as}`]: `${overPath}[${i}]` },
        suffix: `#${i}`,
      });
    });
  } else {
    iterations.push({ scope: {}, scopePaths: {}, suffix: '' });
  }

  // A page may override the document chrome header (e.g. a per-section band title). Resolved once against
  // the global data (no repeat scope) and shared by every emitted page for this definition.
  let pageHeader: ResolvedBlock[] | undefined;
  if (page.header && page.header.length) {
    pageHeader = [];
    for (const [i, b] of page.header.entries()) {
      pageHeader.push(
        ...(await resolveBlock(b, {
          data: ctx.data, scope: {}, scopePaths: {}, byType: ctx.byType, stage: ctx.stage,
          budget: ctx.budget, path: `page:${page.id}.header[${i}]`,
        }))
      );
    }
  }

  const out: ResolvedPage[] = [];
  for (const iter of iterations) {
    if (page.visibleWhen) {
      const v = await evaluateBinding(page.visibleWhen, ctx.data, iter.scope, ctx.budget, `page:${page.id}`);
      if (!isTruthy(v)) continue;
    }
    const blocks: ResolvedBlock[] = [];
    for (const [i, b] of page.blocks.entries()) {
      blocks.push(
        ...(await resolveBlock(b, {
          data: ctx.data,
          scope: iter.scope,
          scopePaths: iter.scopePaths,
          byType: ctx.byType,
          stage: ctx.stage,
          budget: ctx.budget,
          path: `page:${page.id}.blocks[${i}]`,
        }))
      );
    }
    // A page that resolved to nothing is not a blank sheet of paper.
    if (blocks.length === 0) continue;
    out.push({ id: page.id + iter.suffix, mode: page.mode, blocks, ...(pageHeader ? { header: pageHeader } : {}) });
  }
  return out;
}

/** Parse a #rgb / #rrggbb hex to [r,g,b] 0-255. */
function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
const toHex = (rgb: [number, number, number]): string =>
  '#' + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
/** Toward black by `amount` (0-1). */
const darken = (hex: string, amount: number): string =>
  toHex(hexToRgb(hex).map((v) => v * (1 - amount)) as [number, number, number]);
/** Toward white by `amount` (0-1). */
const lighten = (hex: string, amount: number): string =>
  toHex(hexToRgb(hex).map((v) => v + (255 - v) * amount) as [number, number, number]);

/**
 * The template's theme, with the report's own brand colour applied.
 *
 * White-labelled reports carry the client's colour, which is a per-report fact rather than
 * a property of the template — one template serves every client. So `presentation.themeColor`
 * substitutes for `color.primary` here rather than the template being cloned per client,
 * which is what would otherwise turn one template into seven hundred.
 *
 * `primary` moves, and with it every element that should read as "the brand colour": the header
 * gradient band, its glow, and the bottom-left corner dots — derived here as monochromatic shades
 * of the chosen colour so a client's report is one colour family rather than the chosen primary set
 * against ProData's navy header. Charts and the day/time heatmap key on `--color-primary` in CSS, so
 * they follow automatically. What is NOT touched is legibility and layout — text, borders, surfaces,
 * the accent rule — because letting a colour picker reach those is how a report ends up with white
 * text on a white card. When no colour is chosen, the template's own palette is returned untouched,
 * so a default report looks exactly as designed.
 */
function themeFor(theme: ThemeTokens, data: unknown): ThemeTokens {
  const pres = (data as { presentation?: { themeColor?: unknown; accentColor?: unknown } } | undefined)
    ?.presentation;
  const isHex = (v: unknown): v is string =>
    typeof v === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v);
  const chosen = pres?.themeColor;
  const accent = pres?.accentColor;
  // Neither brand colour chosen → the template's own palette, untouched.
  if (!isHex(chosen) && !isHex(accent)) return theme;

  const color = { ...theme.color };
  if (isHex(chosen)) {
    // `primary` moves, and with it the header band and everything keyed on --color-primary.
    color.primary = chosen;
    // The header band: darkest → dark → bright, a monochromatic run of the chosen colour.
    color.headerFrom = darken(chosen, 0.55);
    color.headerTo = darken(chosen, 0.15);
    color.headerDeep = lighten(chosen, 0.15);
    color.headerGlow = lighten(chosen, 0.45);
    // The bottom-left halftone dots — a light tint of the brand colour.
    color.cornerDot = lighten(chosen, 0.62);
  }
  // The ACCENT — the header underline and the campaign ID# text (--color-accent). The one other
  // brand colour a white-label report carries; legibility and layout are still left alone.
  if (isHex(accent)) color.accent = accent;
  return { ...theme, color };
}

/**
 * Resolve a template against report data for one lifecycle stage.
 *
 * The stage defaults to the data model's own `lifecycle`, which is what makes C5
 * work: pre-flight, interim and post-flight come out of one document.
 */
export async function resolveTemplate(
  template: Template,
  data: { lifecycle: Lifecycle } & Record<string, unknown>,
  registry: BlockManifest[],
  options: ResolveOptions = {}
): Promise<ResolvedDocument> {
  const stage = options.lifecycle ?? data.lifecycle;
  const budget = options.budget ?? DEFAULT_BUDGET;

  /*
   * A template declares which lifecycle stages it supports. Rendering it at a stage it
   * does not support produced a plausible-looking document rather than an error: the
   * DOOH pre-flight template, which declares PREFLIGHT only, rendered happily at
   * POSTFLIGHT because none of its blocks happened to be stage-restricted. The output
   * was a pre-flight report labelled as a post-flight one.
   *
   * The declaration is meaningless if nothing enforces it.
   */
  if (!template.lifecycle.includes(stage)) {
    throw new BindingError(
      `template "${template.id}" does not support lifecycle ${stage} ` +
      `(declares ${template.lifecycle.join(', ')})`,
      '', `template:${template.id}`
    );
  }

  const byType = new Map(registry.map((m) => [m.type, m]));

  const chrome = async (blocks: BlockInstanceT[], kind: string) => {
    const out: ResolvedBlock[] = [];
    for (const [i, b] of blocks.entries()) {
      out.push(
        ...(await resolveBlock(b, {
          data, scope: {}, scopePaths: {}, byType, stage, budget, path: `${kind}[${i}]`,
        }))
      );
    }
    return out;
  };

  const pages: ResolvedPage[] = [];
  for (const p of template.pages) {
    pages.push(...(await resolvePage(p, { data, byType, stage, budget })));
  }

  return {
    lifecycle: stage,
    theme: themeFor(template.theme, data),
    header: await chrome(template.chrome.header, 'chrome.header'),
    footer: await chrome(template.chrome.footer, 'chrome.footer'),
    pages,
  };
}
